import { inject, Injectable } from '@angular/core';
import {
  ComponentsService,
  PlaylistComponentItem,
  PlaylistComponentInfo,
} from '../../services/components.service';
import {
  BasePlaylistService,
  BasePlaylistTemplateItem,
} from './base-playlist.service';
import { cloneDeep } from 'lodash';
import { MediaSelectorService } from './media-selector.service';
import { LayoutMetadata } from './layout-metadata.service';
import { LayoutHugBoxService } from './layout-hug-box.service';

export type UserComponentTemplateItem = BasePlaylistTemplateItem & {
  layout?: {
    layoutId: string;
  };
};

// App-level default repeat count used only to size a freshly-added
// component's initial box when its default layout repeats (e.g. a fresh
// Countdown's visible block count — see LayoutCounterComponent's
// blockCountForSave(), which defaults to DOWN with hours/minutes/seconds
// all on: 1 (days) + 3 = 4). Layout metadata only describes HOW a layout
// repeats (LayoutMetadata.repeating), never how many repeats a fresh
// component starts with — that's this app's own default state, not
// something the layout metadata file can tell us.
const DEFAULT_REPEAT_COUNT_BY_TYPE: Record<string, number> = {
  'rise-layout-counter': 4,
};

@Injectable({
  providedIn: 'root',
})
export class UserComponentsService extends BasePlaylistService {
  private layoutHugBoxService = inject(LayoutHugBoxService);

  playlistComponents: PlaylistComponentItem[] =
    ComponentsService.USER_COMPONENTS_ARRAY;

  private _getBaseAttributes(type: string, tagName?: string) {
    const attributes = {
      id: 'uc-' + type + '-' + Date.now(),
      tagName: tagName ?? type,
      userComponent: true,
    };

    if (this.getComponentByType(type).playUntilDone) {
      attributes['play-until-done'] = true;
    }

    return attributes;
  }

  private _addUserComponentsItem(
    item,
    editItem: boolean = true,
    fileType: string = '',
    options?: any,
  ) {
    this.playlistItems.unshift(item);

    // save the added component to attribute data
    this.attributeDataService.addUserComponent(cloneDeep(item));

    const componentType = this._getComponentTypeForTracking(item, fileType);

    this.analyticsFactory.track('User Component Added', {
      componentType,
    });

    if (editItem) {
      this.editComponent(item, false, options);
    }
  }

  protected getComponentObject(
    item: UserComponentTemplateItem,
  ): PlaylistComponentInfo {
    const component: PlaylistComponentInfo = {
      type: item.tagName,
      id: item.id,
      layoutId: item.layout?.layoutId,
    };

    if (item['isLogo'] === true) {
      component.isLogo = true;
    }

    return component;
  }

  addItem(type: string, options?: any): void {
    if (type === 'rise-folder') {
      if (
        options &&
        options.fileType &&
        options.fileType ===
          UserComponentsService.FOLDER_TYPE.GOOGLE_PHOTOS_ALBUM
      ) {
        return this.showGooglePhotos();
      }
      if (options && options.fileType && options.fileType === 'canva') {
        return this.showCanvaConnect();
      }

      return this.showMediaSelector(options);
    }

    if (type === 'rise-stock-library') {
      return this.showMediaSelector({
        stockLibrary: true,
      });
    }

    if (type === 'rise-canva-connect') {
      return this.showCanvaConnect();
    }

    // Note, for rise layouts, the component type is full name like 'rise-layout-weather'
    // so we don't need to use the getComponentByTypeOrLayoutId() method here
    const component = this.getComponentByType(type);
    const baseAttributes = this._getBaseAttributes(type, component.tagName);
    const defaultAttributes = cloneDeep(component.defaultAttributes) || {};
    const attributeOverrides = cloneDeep(options?.attributes) || {};

    const item = {
      ...baseAttributes,
      ...defaultAttributes,
      ...attributeOverrides,
    };

    // Insert synchronously, at whatever box it already has (unset —
    // common-template falls back to its generic 300x200 default,
    // centered — see _applyAddTimeHugBox's note on why that matters).
    // No async gap before insertion means two overlapping addItem calls
    // always unshift (and select) in click order, the same guarantee this
    // service had before layout components needed a metadata lookup at
    // all. If this is a rise-layout-* component, its hug box is patched
    // in asynchronously right after — see _applyAddTimeHugBox.
    this._addUserComponentsItem(item, true, '', options);

    const layoutId = item['layout']?.layoutId;
    if (!item['customization'] && layoutId) {
      void this._applyAddTimeHugBox(
        item['id'],
        layoutId,
        type,
        item['services'],
      );
    }
  }

  // Gives a newly-added rise-layout-* component a box that hugs the aspect
  // ratio its default layout was designed for, instead of staying at
  // common-template's generic 300x200 default. Patched in asynchronously,
  // after addItem's synchronous insert, reusing the exact same box-fit
  // logic a layout switch uses (LayoutHugBoxService) — including its
  // generation guard (a later layout switch on this same component always
  // wins over this add-time patch) and its componentBoxUpdated broadcast
  // (the just-added component is already selected by the time this
  // resolves, and the selection overlay only re-measures on an explicit
  // highlight, so without that broadcast it would keep showing the stale
  // default box until the user deselects and reselects).
  //
  // The call into LayoutHugBoxService is made immediately, not after a
  // metadata lookup of our own: that is where the generation guard is
  // reserved, so a layout switch the user makes while the metadata is still
  // loading is always the later request and wins. The repeat count, which
  // depends on the metadata, is resolved by a callback the service invokes
  // once it has the metadata.
  //
  // A brief flash of the generic default box while this resolves is the
  // accepted tradeoff for keeping insertion itself synchronous — see
  // addItem. LayoutHugBoxService's own fallback-to-canvas-center anchoring
  // means the box only changes size here, not position: common-template's
  // 300x200 default is itself centered on the canvas, so there's no jump.
  private async _applyAddTimeHugBox(
    componentId: string,
    layoutId: string,
    componentType: string,
    services: unknown,
  ): Promise<void> {
    await this.layoutHugBoxService.applyHugBoxForLayoutSwitch(
      componentId,
      layoutId,
      (metadata) => this._resolveRepeatCount(metadata, componentType, services),
    );
  }

  // A caller can override the item's initial repeat-driving attribute (e.g.
  // addItem('rise-layout-counter', { attributes: { services: [{ attributes:
  // { blockCount: 8 } }] } })) via addItem's options.attributes — read that
  // first so the add-time box matches what will actually render. metadata's
  // repeating.countProperty tells us which attribute key to look for; we
  // have no opinion on it beyond that. Falls back to this app's own default
  // state for a fresh component of this type (DEFAULT_REPEAT_COUNT_BY_TYPE)
  // when no override is present.
  private _resolveRepeatCount(
    metadata: LayoutMetadata | null | undefined,
    componentType: string,
    services: unknown,
  ): number {
    const countProperty = metadata?.repeating?.countProperty;
    const overrideValue = countProperty
      ? (services as any[] | undefined)?.[0]?.attributes?.[countProperty]
      : undefined;

    if (
      typeof overrideValue === 'number' &&
      isFinite(overrideValue) &&
      overrideValue > 0
    ) {
      return overrideValue;
    }

    return DEFAULT_REPEAT_COUNT_BY_TYPE[componentType] ?? 1;
  }

  addUserComponentsMediaItems(
    itemsToAdd: any[],
    editItem?: boolean,
    fileType?: string,
  ) {
    // we're only supporting single file, so the item to add is always first index
    const itemToAdd = itemsToAdd[0];

    const baseAttributes = this._getBaseAttributes('rise-folder');
    const itemAttributes = itemToAdd.attributes
      ? cloneDeep(itemToAdd.attributes)
      : {};

    const item = { ...baseAttributes, ...itemAttributes };

    this._addUserComponentsItem(item, editItem, fileType);
  }

  showMediaSelector(options?: any) {
    super.showMediaSelector(options);

    this.mediaSelectorService.onUserComponentAdd =
      this.addUserComponentsMediaItems.bind(this);
  }

  showGooglePhotos() {
    this.addUserComponentsMediaItems(
      [
        {
          attributes: this.getGooglePhotosItemAttributes(),
        },
      ],
      true,
    );
  }

  showCanvaConnect() {
    this.addUserComponentsMediaItems(
      [
        {
          attributes: this.getCanvaItemAttributes(),
        },
      ],
      true,
    );
  }

  removeUserComponent(id) {
    const key = this.playlistItems.findIndex((item) => item.id === id);

    if (key === -1) {
      return;
    }

    const item = this.playlistItems[key];

    if (!item.userComponent) {
      return;
    }

    // Remove Authenticated Web Page credential, if any
    const component = this.attributeDataService.getComponent(id);
    if (
      this.webPageService.isWebPageComponent(item.tagName) &&
      component?.credential
    ) {
      this.webPageService.removeCredential(component?.credential);
    }

    this.playlistItems.splice(key, 1);
    this.attributeDataService.removeUserComponent(id);
    this._trackItemRemoval(item);
  }

  duplicateUserComponent(id: string, editItem: boolean = true): void {
    const key = this.playlistItems.findIndex((item) => item.id === id);
    if (key === -1) {
      return;
    }

    const item = this.playlistItems[key];
    if (!item?.userComponent) {
      return;
    }

    // Position lives on `templateAttributeData.components` after drag
    // (saveComponentPosition). Prefer that row: getComponent() consults the
    // blueprint first and can return a stale definition without customization.
    const attributeData =
      this.templateEditorService.presentation?.templateAttributeData;
    const attributeDataRow =
      attributeData?.components?.find((c: { id: string }) => c.id === id) ??
      null;
    const storeComponent = this.attributeDataService.getComponent(id);
    const source =
      attributeDataRow && typeof attributeDataRow === 'object'
        ? attributeDataRow
        : storeComponent &&
            typeof storeComponent === 'object' &&
            storeComponent.userComponent
          ? storeComponent
          : item;
    const duplicate = cloneDeep(source);
    duplicate.id = this._nextUserComponentId(item);
    duplicate.userComponent = true;
    delete duplicate.editing;

    if (
      this.webPageService.isWebPageComponent(item.tagName) &&
      duplicate.attributes?.credential
    ) {
      duplicate.attributes.credential = this.webPageService.copyCredential(
        duplicate.attributes.credential,
      );
    }

    this.playlistItems.splice(key, 0, duplicate);

    const duplicateForStore = cloneDeep(duplicate);
    if (attributeData?.components) {
      const dataIdx = attributeData.components.findIndex((c) => c.id === id);
      if (dataIdx !== -1) {
        attributeData.components.splice(dataIdx, 0, duplicateForStore);
      } else {
        this.attributeDataService.addUserComponent(duplicateForStore);
      }
    } else {
      this.attributeDataService.addUserComponent(duplicateForStore);
    }

    const folderSubtype = this._folderSubtypeForTracking(item);
    const componentType = this._getComponentTypeForTracking(
      item,
      folderSubtype,
    );

    this.analyticsFactory.track('User Component Added', {
      componentType,
    });

    // Match editHighlightedComponent: reset panel stack / DOM when something was
    // already open, or showNextPage swaps into a blank strip.
    let stored = this.attributeDataService.getComponent(duplicate.id);
    let editPayload =
      stored?.userComponent && stored.tagName
        ? {
            id: stored.id,
            type: stored.tagName,
            layoutId: stored.layout?.layoutId,
          }
        : this.getComponentObject(duplicate);

    if (this.componentsService.selected) {
      this.componentsService.backToList();
    }

    if (editItem) {
      this.componentsService.editComponent(editPayload, { playlist: false });
    }
  }

  private _nextUserComponentId(item: any): string {
    let typeKey = item.tagName;
    if (item.tagName === 'rise-layout' && item.layout?.type === 'custom') {
      typeKey = item.layout.baseComponent || item.tagName;
    } else if (item.tagName === 'rise-layout' && item.layout?.layoutId) {
      typeKey =
        ComponentsService.LAYOUT_DIRECTIVES_MAP[item.layout.layoutId] ||
        item.tagName;
    }
    return `uc-${typeKey}-${Date.now()}`;
  }

  private _folderSubtypeForTracking(item: any): string | undefined {
    const type = item.tagName;
    if (type?.toLowerCase() !== 'rise-folder') {
      return undefined;
    }

    const itemType = item.type || item.attributes?.type;
    if (itemType === MediaSelectorService.FOLDER_TYPE.STORAGE_FILE) {
      const source = item.source || item.attributes?.source;
      return source ? this.storageManagerService.getFileType(source) : 'file';
    }
    if (itemType === MediaSelectorService.FOLDER_TYPE.STORAGE_FOLDER) {
      return 'folder';
    }
    return itemType;
  }

  _trackItemRemoval(item) {
    const folderSubtype = this._folderSubtypeForTracking(item);
    const componentType = this._getComponentTypeForTracking(
      item,
      folderSubtype,
    );

    this.analyticsFactory.track('User Component Removed', {
      componentType,
    });
  }

  _getComponentTypeForTracking(item, fileType?: string) {
    const type = item.tagName;

    if (type.toLowerCase() === 'rise-folder' && fileType) {
      return `rise-folder/${fileType}`;
    }

    if (
      type.toLowerCase() === 'rise-layout' &&
      item.layout?.type === 'custom'
    ) {
      return `rise-layout/custom/${item.layout.baseComponent}`;
    }

    if (type.toLowerCase() === 'rise-layout' && item.layout?.layoutId) {
      return `rise-layout/${item.layout.layoutId}`;
    }

    return type;
  }
}
